import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import apiService from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import StripePaymentForm from './StripePaymentForm';

// Initialize Stripe with the publishable key
const stripePromise = loadStripe(`${process.env.STRIPE_PUBLISHABLE_KEY}`);

// Credit package options
const CREDIT_PACKAGES = [
  { id: 'basic', credits: 100, price: 999, popular: false },
  { id: 'standard', credits: 500, price: 3999, popular: true },
  { id: 'premium', credits: 1000, price: 6999, popular: false },
  { id: 'enterprise', credits: 5000, price: 29999, popular: false }
];

const CreditPurchase = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [creditBalance, setCreditBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Fetch credit balance and payment methods on component mount
  useEffect(() => {
    const initData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchCreditBalance(),
          fetchTransactions(),
          fetchPaymentMethods()
        ]);
      } catch (err) {
        console.error('Error initializing credit data:', err);
        setError(t('credits.errors.initialization'));
      } finally {
        setLoading(false);
      }
    };

    initData();
  }, [t]);

  const fetchCreditBalance = async () => {
    try {
      const response = await apiService.getCreditBalance();
      setCreditBalance(response.balance || 0);
    } catch (err) {
      console.error('Error fetching credit balance:', err);
      addToast({
        type: 'error',
        message: t('credits.errors.fetchBalance')
      });
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await apiService.getCreditTransactions();
      setTransactions(response.transactions || []);
    } catch (err) {
      console.error('Error fetching credit transactions:', err);
      // Don't show a toast for this as it's not critical
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await apiService.getPaymentMethods();
      setPaymentMethods(response.data || []);
      
      // Set the default payment method if available
      const defaultMethod = response.data?.find(method => method.isDefault);
      if (defaultMethod) {
        setSelectedPaymentMethod(defaultMethod.id);
      } else if (response.data?.length > 0) {
        setSelectedPaymentMethod(response.data[0].id);
      }
    } catch (err) {
      console.error('Error fetching payment methods:', err);
      // Don't set an error here, as it's not critical for viewing credits
    }
  };

  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg);
    
    // If user has payment methods, show purchase confirmation
    // Otherwise, show payment form
    if (paymentMethods.length > 0) {
      setShowPaymentForm(false);
    } else {
      setShowPaymentForm(true);
    }
  };

  const handlePaymentMethodAdded = async (response) => {
    // Refresh payment methods
    await fetchPaymentMethods();
    setShowPaymentForm(false);
    
    // If a package was selected, proceed with purchase
    if (selectedPackage) {
      handlePurchase();
    }
  };

  const handlePurchase = async () => {
    if (!selectedPackage) return;
    
    setProcessing(true);
    try {
      await apiService.purchaseCredits(
        selectedPackage.price,
        selectedPackage.credits
      );
      
      addToast({
        type: 'success',
        message: t('credits.success.purchased', { 
          credits: selectedPackage.credits,
          price: formatPrice(selectedPackage.price)
        })
      });
      
      // Refresh credit balance and transactions
      await Promise.all([
        fetchCreditBalance(),
        fetchTransactions()
      ]);
      
      // Reset state
      setSelectedPackage(null);
    } catch (err) {
      console.error('Purchase error:', err);
      setError(err.message || t('credits.errors.purchase'));
      addToast({
        type: 'error',
        message: err.message || t('credits.errors.purchase')
      });
    } finally {
      setProcessing(false);
    }
  };

  const formatPrice = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount / 100);
  };

  const formatCardInfo = (method) => {
    if (!method) return '';
    return `${method.card.brand.toUpperCase()} •••• ${method.card.last4}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {error && (
        <div className="p-4 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {/* Credit Balance */}
      <div className="p-6 border border-gray-200 rounded-md bg-blue-50">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">{t('credits.yourBalance')}</h2>
            <p className="text-gray-600">{t('credits.useForProcessing')}</p>
          </div>
          <div className="text-3xl font-bold text-blue-600">
            {creditBalance} <span className="text-sm font-normal">{t('credits.credits')}</span>
          </div>
        </div>
      </div>

      {/* Credit Packages */}
      <div>
        <h2 className="text-xl font-semibold mb-4">{t('credits.buyCredits')}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {CREDIT_PACKAGES.map((pkg) => (
            <div 
              key={pkg.id} 
              className={`p-6 border rounded-md relative ${
                selectedPackage?.id === pkg.id 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-blue-300'
              } transition-colors cursor-pointer`}
              onClick={() => handleSelectPackage(pkg)}
            >
              {pkg.popular && (
                <div className="absolute top-0 right-0 bg-green-500 text-white px-2 py-1 text-xs font-semibold rounded-bl-md rounded-tr-md">
                  {t('credits.mostPopular')}
                </div>
              )}
              
              <h3 className="text-lg font-medium">{pkg.credits} {t('credits.credits')}</h3>
              <div className="text-2xl font-bold my-2">
                {formatPrice(pkg.price)}
              </div>
              <p className="text-gray-600 mb-4">
                {t('credits.valueProposition', { price: formatPrice(pkg.price / pkg.credits * 100) })}
              </p>
              
              <button
                className={`w-full py-2 px-4 rounded-md ${
                  selectedPackage?.id === pkg.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                } transition-colors`}
              >
                {selectedPackage?.id === pkg.id ? t('credits.selected') : t('credits.select')}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Purchase Confirmation or Payment Form */}
      {selectedPackage && (
        <div className="p-6 border border-gray-200 rounded-md bg-gray-50">
          <h3 className="text-lg font-medium mb-4">
            {t('credits.confirmPurchase', { 
              credits: selectedPackage.credits,
              price: formatPrice(selectedPackage.price)
            })}
          </h3>
          
          {showPaymentForm ? (
            <div>
              <p className="mb-4">{t('credits.addPaymentMethod')}</p>
              <Elements stripe={stripePromise}>
                <StripePaymentForm 
                  onSuccess={handlePaymentMethodAdded}
                  buttonText={t('payment.saveAndPurchase')}
                  processingText={t('payment.processing')}
                />
              </Elements>
              <button
                onClick={() => {
                  setShowPaymentForm(false);
                  setSelectedPackage(null);
                }}
                className="mt-4 text-gray-600 hover:text-gray-800"
              >
                {t('common.cancel')}
              </button>
            </div>
          ) : (
            <div>
              {paymentMethods.length > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('payment.selectPaymentMethod')}
                  </label>
                  <div className="space-y-2">
                    {paymentMethods.map((method) => (
                      <div 
                        key={method.id}
                        className={`p-3 border rounded-md cursor-pointer ${
                          selectedPaymentMethod === method.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 hover:border-blue-300'
                        }`}
                        onClick={() => setSelectedPaymentMethod(method.id)}
                      >
                        <div className="flex items-center">
                          <input
                            type="radio"
                            checked={selectedPaymentMethod === method.id}
                            onChange={() => setSelectedPaymentMethod(method.id)}
                            className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="ml-3">{formatCardInfo(method)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <button
                    onClick={() => setShowPaymentForm(true)}
                    className="mt-2 text-blue-600 hover:text-blue-800"
                  >
                    {t('payment.useNewCard')}
                  </button>
                </div>
              )}
              
              <div className="flex space-x-4">
                <button
                  onClick={handlePurchase}
                  disabled={processing || (paymentMethods.length > 0 && !selectedPaymentMethod)}
                  className={`py-2 px-4 rounded-md ${
                    processing || (paymentMethods.length > 0 && !selectedPaymentMethod)
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  } transition-colors`}
                >
                  {processing 
                    ? t('credits.processing') 
                    : t('credits.confirmPurchaseButton', { price: formatPrice(selectedPackage.price) })}
                </button>
                
                <button
                  onClick={() => setSelectedPackage(null)}
                  className="py-2 px-4 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Transaction History */}
      <div>
        <h2 className="text-xl font-semibold mb-4">{t('credits.transactionHistory')}</h2>
        
        {transactions.length === 0 ? (
          <div className="p-6 border border-gray-200 rounded-md bg-gray-50 text-center">
            <p className="text-gray-600">{t('credits.noTransactions')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('credits.date')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('credits.description')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('credits.amount')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('credits.balance')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(transaction.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {transaction.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`font-medium ${
                        transaction.amount > 0 
                          ? 'text-green-600' 
                          : 'text-red-600'
                      }`}>
                        {transaction.amount > 0 ? '+' : ''}{transaction.amount}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {transaction.balanceAfter}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditPurchase;
